/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';

import { Block, ErrorMessage } from '@atoms';
import { SlugField, TitleField, TextField, CheckboxField, MinisiteField, CampaignField, DateField } from '@molecules';
import MediaField from '@organisms/MediaField';
import { useWatchFormValues } from '@hooks';
import DocumentField from '@organisms/DocumentField';
import { ASSET_TYPE } from '@api/assets/constants';
import RichTextField from '@molecules/RichTextField';

import Form from '../Form';
import RelatedContentField from '../RelatedContentField';

import AssetTypeField from './AssetTypeField';
import AssetLanguageSelect from './AssetLanguageSelect';
import { FORM_FIELD, FORM_FIELDS, DEFAULT_VALUES, FORM_VALUES_TO_WATCH } from './constants';

const MainBlock = ({ formValues, initialValues, isGated, isKidsMiniSite }) => {
  const initialMinisite = initialValues[FORM_FIELD.MINISITE.name];

  const showMedia = [ASSET_TYPE.PPT.id, ASSET_TYPE.PDF.id].includes(formValues[FORM_FIELD.TYPE.name]?.contentId);

  return (
    <Block spaced>
      <MinisiteField {...FORM_FIELD.MINISITE} initialEvent={initialMinisite} />
      {isKidsMiniSite && <AssetLanguageSelect {...FORM_FIELD.LANGUAGESELECT} />}
      <AssetTypeField {...FORM_FIELD.TYPE} />
      <TitleField {...FORM_FIELD.TITLE} extra={null} wrapperCol={{ span: 24 }} />
      <SlugField
        {...FORM_FIELD.SLUG}
        generateSlugFrom={!initialValues[FORM_FIELD.SLUG.name] && FORM_FIELD.TITLE.name}
      />
      {/* <TextField multiline {...FORM_FIELD.SEO_DESCRIPTION} /> */}
      <RichTextField {...FORM_FIELD.SEO_DESCRIPTION} />
      <MediaField {...FORM_FIELD.THUMBNAIL_FEATURE_IMAGE} />
      {showMedia ? (
        <DocumentField
          {...FORM_FIELD.ASSET}
          buttonText="CHOOSE AN ASSET"
          // This is ugly, but it was  fast. Redo later with className or variant
          rowProps={{ align: 'middle', justify: 'space-between', style: { flexDirection: 'row-reverse' } }}
        />
      ) : (
        <TextField {...FORM_FIELD.ASSET} />
      )}
      <CheckboxField {...FORM_FIELD.GATED_CONTENT} />
      <DateField {...FORM_FIELD.MINISITE_DATE} />
      {isGated && <CampaignField {...FORM_FIELD.CAMPAIGN} />}
      {showMedia && <CheckboxField {...FORM_FIELD.DOWNLOAD_ONLY} />}
    </Block>
  );
};

const RelatedContentBlock = ({ form, currentMiniSiteId }) => {
  const miniSite = currentMiniSiteId && currentMiniSiteId.toLowerCase().replace(/\s/g, '');
  const FILTERED_FIELDS = FORM_FIELDS.filter(function (field) {
    return field.miniSiteType === miniSite || field.miniSiteType === 'all';
  });

  return (
    <Block spaced>
      <RelatedContentField form={form} fields={FILTERED_FIELDS} />
    </Block>
  );
};

const AssetForm = ({
  initialValues = {},
  error,
  form,
  setGlobalAssetLanguage,
  setCurrentMiniSiteId,
  currentMiniSiteId,
}) => {
  const [formValues, watchFormValues] = useWatchFormValues(FORM_VALUES_TO_WATCH, initialValues);
  const [isGated, setIsGated] = useState(initialValues.gatedContent || false);

  const kidsAthletics = 'Kids Athletics';
  const [isKidsMiniSite, setIsKidsMiniSite] = useState(initialValues?.minisite?.name === kidsAthletics || false);

  const handleValuesChange = React.useCallback(
    (change, allChanges) => {
      if (FORM_FIELD.GATED_CONTENT.name in change) {
        setIsGated(change[FORM_FIELD.GATED_CONTENT.name]);
      }

      if (FORM_FIELD.MINISITE.name in change) {
        setCurrentMiniSiteId(change[FORM_FIELD.MINISITE.name].name);

        if (change[FORM_FIELD.MINISITE.name].name === kidsAthletics) {
          setIsKidsMiniSite(true);
        } else {
          setIsKidsMiniSite(false);
        }
      }

      if (change.language && initialValues.language) {
        setGlobalAssetLanguage(change.language);
        const location = window.location.pathname;
        const path = `/${location.split('/')[1]}/${location.split('/')[2]}/${change.language}`;
        window.location.href = path;
      }

      watchFormValues(change, allChanges);
    },
    [initialValues, setGlobalAssetLanguage, watchFormValues, setCurrentMiniSiteId],
  );

  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onValuesChange={handleValuesChange}>
      <ErrorMessage error={error} variant="block" spaced />

      <MainBlock
        formValues={formValues}
        initialValues={initialValues}
        isGated={isGated}
        isKidsMiniSite={isKidsMiniSite}
        // setCurrentMiniSiteId={setCurrentMiniSiteId}
      />

      <RelatedContentBlock form={form} currentMiniSiteId={currentMiniSiteId} />
    </Form>
  );
};

AssetForm.useForm = () => Form.useForm(FORM_FIELDS, DEFAULT_VALUES);
AssetForm.FORM_FIELD = FORM_FIELD;

export default AssetForm;
